// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2024 Vic Zhu <viczhu2014@gmail.com>

import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useDataProvider } from 'react-admin'
import { usePetDevicesWithHistory } from './usePetDevicesWithHistory'

interface UseEarliestMetricDateResult {
  earliestDate: Date | null
  isLoading: boolean
  error: any
}

/**
 * Hook to fetch the earliest metric date across all devices for a pet
 * Returns the earliest device start_time as the data origin
 *
 * IMPORTANT: We use device.start_time (sensor activation time) NOT pet.createdAt
 * because pet.createdAt can change when users switch phones, but device.start_time
 * represents the true CGM sensor activation date and should remain constant.
 *
 * Bug fix: PROD-1111 - Device data history was being lost when users switched phones
 * because the system was using pet.createdAt (which gets reset on phone switch)
 * instead of device.start_time (which is the actual sensor activation time)
 */
export function useEarliestMetricDate(petId: string | number | null): UseEarliestMetricDateResult {
  const dataProvider = useDataProvider()

  // Fetch all devices for this pet (including historical devices)
  const { data: devices = [], isLoading: devicesLoading } = usePetDevicesWithHistory(petId)

  // Find the earliest device start_time across all devices
  const earliestDeviceStartTime = useMemo(() => {
    if (devices.length === 0) return null

    const startTimes = devices
      .filter((d) => d.start_time && d.start_time > 0)
      .map((d) => d.start_time as number)

    if (startTimes.length === 0) return null

    const minStartTime = Math.min(...startTimes)
    return new Date(minStartTime * 1000)
  }, [devices])

  // The earliest metric is only a FALLBACK for pets whose devices carry no
  // start_time (see earliestDate below), so it is fetched only then — and ONE
  // DEVICE PER REQUEST, never as `device IN (…)`.
  //
  // `device IN (…) ORDER BY timestamp ASC LIMIT 1` makes Postgres walk the
  // global timestamp index from the oldest row in `metrics`, filtering for the
  // pet's devices. It is slowest exactly when this fallback runs: a device with
  // no start_time has, in practice, NO readings (prod 2026-10-12: 1 of 141 such
  // devices ever reported one), so nothing matches and the walk covers the
  // whole table until the 15 s statement timeout. Prod logged 14 of these.
  //
  // `device = <id>` is served by idx_metrics_device_timestamp instead: 1.5 ms
  // measured on prod, and an empty device answers immediately. Same shape as
  // useDeviceWearPeriods' ASC edge.
  const fallbackDeviceIds = useMemo(
    () => (earliestDeviceStartTime === null ? devices.map((d) => d.id) : []),
    [devices, earliestDeviceStartTime],
  )

  const results = useQueries({
    queries: fallbackDeviceIds.map((id) => ({
      queryKey: ['earliest-metric', String(id)],
      queryFn: async (): Promise<number | null> => {
        const { data } = await dataProvider.getList('metrics', {
          // Explicit depth (L-089): REST default 2 embeds every relation per row; only ids are read.
          meta: { params: { depth: 0 } },
          filter: { device: id },
          sort: { field: 'timestamp', order: 'ASC' },
          pagination: { page: 1, perPage: 1 },
        })
        const ts = data?.[0]?.timestamp
        return typeof ts === 'number' && Number.isFinite(ts) && ts > 0 ? ts : null
      },
      staleTime: 60_000,
    })),
  })

  const metricsLoading = results.some((r) => r.isPending && r.fetchStatus !== 'idle')
  const error = results.find((r) => r.error)?.error ?? null
  // Stable across renders: useQueries returns a new array identity every render.
  const earliestMetricTs = results.reduce<number | null>((min, r) => {
    const ts = r.data
    if (typeof ts !== 'number') return min
    return min === null || ts < min ? ts : min
  }, null)

  const earliestDate = useMemo(() => {
    // Use device start_time as the authoritative start date floor.
    // No valid CGM data can exist before sensor activation, so any metric
    // with a timestamp earlier than start_time is bogus (e.g., a raw device
    // offset stored as a Unix timestamp → dates like 1970).
    if (earliestDeviceStartTime) {
      return earliestDeviceStartTime
    }
    // Fallback: no device start_time available, use earliest metric
    return earliestMetricTs !== null ? new Date(earliestMetricTs * 1000) : null
  }, [earliestMetricTs, earliestDeviceStartTime])

  return {
    earliestDate,
    isLoading: devicesLoading || metricsLoading,
    error,
  }
}
